import { mount, unmount } from "svelte";
import { writable, type Writable } from "svelte/store";
import type { VideoBox } from "../../Space/VideoBox";
import AvatarVideoOverlay from "../../Components/Video/AvatarVideoOverlay.svelte";
import type { GameScene } from "../Game/GameScene";
import { waScaleManager, WaScaleManagerEvent } from "../Services/WaScaleManager";

export class AvatarVideoDisplay {
  private readonly element: HTMLDivElement;
  private readonly distanceVisible: Writable<boolean> = writable(false);
  private readonly component: ReturnType<typeof mount>;
  private x = 0;
  private y = 0;
  private depth = 0;
  private destroyed = false;

  private readonly onZoomChanged = (): void => {
    this.applyTransform();
  };

  constructor(
    private readonly scene: GameScene,
    videoBox: VideoBox,
    selfPreview: boolean,
  ) {
    this.element = document.createElement("div");
    this.element.className = "avatar-video-display";
    this.element.style.position = "absolute";
    this.element.style.left = "0";
    this.element.style.top = "0";
    this.element.style.width = "84px";
    this.element.style.height = "47px";
    this.element.style.pointerEvents = "none";
    this.element.style.transformOrigin = "center bottom";
    this.element.style.willChange = "transform";
    this.element.ariaHidden = "true";

    this.scene.usernameDomLayer.addElement(this.element);
    this.component = mount(AvatarVideoOverlay, {
      target: this.element,
      props: {
        videoBox,
        distanceVisible: this.distanceVisible,
        selfPreview,
      },
    });
    this.scene.game.events.on(WaScaleManagerEvent.ZoomChanged, this.onZoomChanged);
  }

  public setPosition(x: number, y: number): void {
    if (this.x === x && this.y === y) return;
    this.x = x;
    this.y = y;
    this.applyTransform();
  }

  public setDepth(depth: number): void {
    if (this.depth === depth) return;
    this.depth = depth;
    this.element.style.zIndex = `${Math.round(depth)}`;
  }

  public setVisible(visible: boolean): void {
    this.distanceVisible.set(visible);
  }

  public destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.game.events.off(WaScaleManagerEvent.ZoomChanged, this.onZoomChanged);
    unmount(this.component).catch((error: unknown) => {
      console.warn("Avatar video overlay could not be unmounted cleanly.", error);
    });
    this.element.remove();
  }

  private applyTransform(): void {
    const scale = 1 / Math.max(waScaleManager.zoomModifier, 0.001);
    this.element.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) translate(-50%, -100%) scale(${scale})`;
  }
}
